/**
 * QR Code ID Encoding/Decoding Utility
 *
 * This utility decodes student IDs from QR codes that were encoded
 * to obfuscate the incremental pattern.
 *
 * Algorithm:
 * 1. Parse from base36
 * 2. Remove offset
 * 3. Reverse bit rotation
 * 4. XOR with secret key to get original ID
 */

// Configuration constants (must match the encoding side)
const XOR_KEY = 0x5a3c9e7f; // Secret XOR key
const OFFSET = 314159; // Offset value
const BIT_ROTATION = 7; // Number of bits to rotate

/**
 * Rotate bits right within 32-bit integer
 */
function rotateRight(n: number, bits: number): number {
  return ((n >>> bits) | (n << (32 - bits))) >>> 0;
}

/**
 * Decode an obfuscated string back to the original student ID
 * @param encoded - The obfuscated base36 string
 * @returns The original numeric student ID, or null if invalid
 */
export function decodeStudentId(encoded: string): number | null {
  try {
    if (!encoded || encoded.trim() === '') {
      return null;
    }

    // Validate input contains only valid base36 characters
    if (!/^[A-Za-z0-9]+$/.test(encoded)) {
      return null;
    }

    // Step 1: Parse from base36
    let decoded = parseInt(encoded.toLowerCase(), 36);

    if (isNaN(decoded) || decoded <= 0) {
      return null;
    }

    // Step 2: Remove offset
    decoded = decoded - OFFSET;

    if (decoded < 0) {
      return null;
    }

    // Step 3: Reverse bit rotation
    decoded = rotateRight(decoded >>> 0, BIT_ROTATION);

    // Step 4: XOR with secret key to get original ID
    decoded = (decoded ^ XOR_KEY) >>> 0;

    // Convert from unsigned to signed if needed
    if (decoded > 0x7fffffff) {
      decoded = decoded - 0x100000000;
    }

    // Validate the result
    if (decoded <= 0 || !Number.isInteger(decoded)) {
      return null;
    }

    return decoded;
  } catch {
    return null;
  }
}

/**
 * Parse student ID from URL parameter.
 * Supports both encoded IDs and legacy plain numeric IDs.
 * @param idParam - The ID parameter from URL (encoded or plain numeric)
 * @returns The decoded student ID, or null if invalid
 */
export function parseStudentIdFromUrl(idParam: string | undefined): string | null {
  if (!idParam) {
    return null;
  }

  const trimmed = idParam.trim();

  // First, check if it's a plain numeric ID (legacy support)
  if (/^\d+$/.test(trimmed)) {
    const numericId = parseInt(trimmed, 10);
    if (numericId > 0) {
      return String(numericId);
    }
  }

  // Try to decode as encoded ID
  const decodedId = decodeStudentId(trimmed);
  if (decodedId !== null) {
    return String(decodedId);
  }

  return null;
}

